import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import { Box, Flex } from 'rebass';
import { func, bool, string } from 'prop-types';
import { ButtonStyled, TextStyled } from '@nike/nike-design-system-components';

import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';
import TableServerSide from '../../../components/tableServerSide/TableServerSide';

import ActionItemList from '../../common/ActionItemList';

import IndexCatalogModal from '../../catalogs/catalogsCreateOrEdit/modals/IndexCatalogModal';
import UnIndexCatalogModal from '../../catalogs/catalogsCreateOrEdit/modals/UnIndexCatalogModal';

import { selectAdminAccount } from '../actions';
import { fetchAllCatalogs, generateValidationReport, deleteAllCatalogs, deleteCatalog } from '../../catalogs/actions';
import { showToast } from '../../../components/toast/actions';

import { getSelectedAccountId } from '../selectors';
import { getAdminHeaderOpenState } from '../../selectors';

import { getAccountCatalogColumns } from './accountCatalogColumns';

import { formatCatalogForCSV } from '../../util';
import { TranslatedCopy } from '../../../intl';

import useModal from '../../../hooks/useModal';

import { MAX_CATALOG_INDEX } from '../../catalogs/constants';

const AccountCatalogs = ({
  selectAdminAccount,
  selectedAccountId,
  isAdminHeaderOpen,
  generateValidationReport,
  showToast
}) => {
  const { id } = useParams();

  const [allCatalogs, setAllCatalogs] = useState([]);

  const [isIndexerModalOpen, setIsIndexerModalOpen] = useState(false);
  const [indexerModalType, setIndexerModalType] = useState(null);
  const [catalogIdsForIndexer, setCatalogIdsForIndexer] = useState([]);
  const [catalogIdsNamesIndexer, setCatalogNamesForIndexer] = useState([]);

  const csvLinkRef = useRef(null);

  const [isActionItemModalOpen, openActionItemModal, closeActionItemModal] = useModal();

  useEffect(() => {
    // force account selection if user refreshes the page
    if (id && !selectedAccountId) {
      selectAdminAccount(id);
    }
  }, []);

  const {
    totalResources,
    handleClearSortAndFilters,
    refreshTableData,
    sortQueryParams,
    filterQueryParams,
    tableInstance: { selectedRowIds = {}, selectedFlatRows, isAllRowsSelected }
  } = useContext(TableServerSideContext);

  useEffect(() => {
    refreshTableData();
  }, [selectedAccountId, isAdminHeaderOpen]);

  const memoizedColumns = useMemo(() => getAccountCatalogColumns(), []);

  const selectedCatalogIds = selectedFlatRows?.reduce((acc, row) => ({ ...acc, [row?.original?.id]: true }), {}) || [];

  const clickCSVLink = async () => setTimeout(() => csvLinkRef?.current?.link.click(), 1000);

  const handleFetchAllCatalogs = async idsOnly => {
    const fetchedCatalogs = await fetchAllCatalogs({ totalResources, sortQueryParams, filterQueryParams });

    if (idsOnly) {
      return fetchedCatalogs.map(catalog => catalog.id);
    }

    return setAllCatalogs(fetchedCatalogs);
  };

  const handleOpenIndexerModal = async type => {
    const selectedCatalogs = selectedFlatRows.map(row => row.original);

    setIndexerModalType(type);
    setIsIndexerModalOpen(true);

    if (selectedFlatRows.length > MAX_CATALOG_INDEX) {
      setCatalogIdsForIndexer([]);
      setCatalogNamesForIndexer([]);
      return;
    }

    setCatalogIdsForIndexer(selectedCatalogs.map(catalog => catalog.id));
    setCatalogNamesForIndexer(selectedCatalogs.map(catalog => catalog.name));
  };

  const handleCloseIndexerModal = () => {
    setIsIndexerModalOpen(false);
    setIndexerModalType(null);
  };

  const actionItems = {
    Download: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Download',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                if (isAllRowsSelected) {
                  toggleIsLoading(true, 'Downloading CSV...');
                  await handleFetchAllCatalogs(false);
                  toggleIsLoading(false);
                }

                clickCSVLink();
                closeActionItemModal();
              }
          }
        ],
        content: () => 'Do you want to proceed with downloading the selected catalog/s?',
        title: 'Confirm Download'
      }
    },
    'Validation Report': {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Download',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                toggleIsLoading(true, 'Downloading Validation Report...');

                const catalogIds = isAllRowsSelected ? await handleFetchAllCatalogs(true) : selectedCatalogIds;
                await generateValidationReport(catalogIds);

                toggleIsLoading(false);

                closeActionItemModal();
              }
          }
        ],
        content: () => 'Would you like to download the Validation Report?',
        title: 'Validation Report'
      }
    },
    Index: { action: () => handleOpenIndexerModal('index') },
    'Un-Index': {
      action: () => handleOpenIndexerModal('unindex')
    },
    '|': { action: () => {} },
    Delete: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Delete',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                toggleIsLoading(true, 'Deleting catalog...');

                try {
                  if (isAllRowsSelected) {
                    await deleteAllCatalogs(filterQueryParams);
                  } else {
                    const ids = Object.keys(selectedCatalogIds);

                    await Promise.all(ids.map(id => deleteCatalog(id)));
                  }

                  refreshTableData();

                  showToast(
                    {
                      type: 'success',
                      message: 'Catalogs deleted'
                    },
                    3000
                  );
                } catch (error) {
                  showToast({
                    message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
                    type: 'error',
                    error
                  });
                }

                toggleIsLoading(false);

                refreshTableData();

                closeActionItemModal();

                showToast({ message: 'Catalogs deleted', type: 'success' }, 5500);
              }
          }
        ],
        content: () => 'Do you want to proceed with deleting the selected catalog/s?',
        title: 'Confirm Deletion'
      }
    }
  };

  const csvLinkData = (isAllRowsSelected ? allCatalogs : selectedFlatRows?.map(row => row.original))?.map(
    ({ id, name, hasDraft, ownerId, accountIds, ...catalog }) => formatCatalogForCSV(catalog, id, name)
  );

  return (
    <Box width='100%'>
      <Flex alignItems='center'>
        <TextStyled appearance='body2' style={{ marginLeft: '5px', marginRight: '10px' }}>
          {`${totalResources}`} results
        </TextStyled>

        <ButtonStyled
          appearance='text'
          background='light'
          ariaLabel='Clear All Filters'
          type='button'
          onClick={handleClearSortAndFilters}
          style={{ marginRight: '10px' }}
        >
          Clear All Filters
        </ButtonStyled>

        <ActionItemList
          selected={selectedRowIds}
          actionItems={actionItems}
          isModalOpen={isActionItemModalOpen}
          openModal={openActionItemModal}
          closeModal={closeActionItemModal}
        />

        <CSVLink data={csvLinkData || []} filename='AccountCatalogs.csv' ref={csvLinkRef} target='_blank' />
      </Flex>

      <TableServerSide
        columns={memoizedColumns}
        url={`v1/catalogs?filter=accountId(${id})`}
        includeRowSelect
        fullWidth
        fullHeight
      />

      <IndexCatalogModal
        isOpen={isIndexerModalOpen && indexerModalType === 'index'}
        closeModal={handleCloseIndexerModal}
        catalogIds={catalogIdsForIndexer}
        catalogNames={catalogIdsNamesIndexer}
        onSuccess={refreshTableData}
      />
      <UnIndexCatalogModal
        isOpen={isIndexerModalOpen && indexerModalType === 'unindex'}
        closeModal={handleCloseIndexerModal}
        catalogIds={catalogIdsForIndexer}
        catalogNames={catalogIdsNamesIndexer}
      />
    </Box>
  );
};

AccountCatalogs.propTypes = {
  selectedAccountId: string.isRequired,
  selectAdminAccount: func.isRequired,
  isAdminHeaderOpen: bool,
  generateValidationReport: func.isRequired,
  showToast: func.isRequired
};

AccountCatalogs.defaultProps = {
  isAdminHeaderOpen: false
};

const mapStateToProps = state => ({
  selectedAccountId: getSelectedAccountId(state),
  isAdminHeaderOpen: getAdminHeaderOpenState(state)
});

export default connect(mapStateToProps, {
  selectAdminAccount,
  generateValidationReport,
  showToast
})(AccountCatalogs);
